import { CustomNavLink, CustomSearchBarConfig, CustomTableColumns, NavLinksGroup, ReferralViewType, TableFilterData } from "@app/models";
import { SelfCareGiverFormData } from "@app/models/user.model";
import { SURVEY_MESSAGES } from "./messages";
import { CustomFieldStatus, CustomFieldTypeOption } from "@app/models/custom-fields.model";
import { environment } from "@environments/environment";

// UI
export const UI_LINKS = {
    TECHNICAL_DOC: 'https://docs.google.com/document/d/1c20k9XtzfpZxgL0PeC1UymrjNjkMcvofsMGYO2vCWgE/edit',
    INTERCOM_DOC: 'https://intercom.help/blooming-health-inc/en',
    TERMS: 'https://gobloominghealth.com/terms-of-service/',
    POLICY: 'https://gobloominghealth.com/privacy-policy/',
    BH: 'https://gobloominghealth.com/'
};
export const TOOLTIP_DELAY = {
    DATA_TABLE: 1000
};

export const DELAYS = {
    ONE_MIN: 60000,
    THIRTY_SEC: 30000,
    TEN_SECS: 10000,
    FIVE_SECS: 5000,
    THREE_SECS: 3000,
    ONE_SEC: 1000,
    TWO_SEC: 2000,
}
// SCRIPT
export const REQ_TIMEOUT: number = 90000;
export const MAX_TIMEOUT: number = 720000;

export const SNACK_TIMEOUT: number = 6000;
export const SNACK_ACTION_TIMEOUT: number = 8000;
export enum AppBreakPoints {
    mobile = 600
};

export const CSV_USERS_HEADERS = [
    'firstname',
    'middlename(optional)',
    'lastname',
    'gender(optional)',
    'primaryphone(textorvoicecalls)',
    'secondaryphone',
    'email',
    'deliverysms',
    'deliveryvoice',
    'deliveryemail',
    'interests(commaseparated)',
    'preferredlanguage',
    'dateofbirth(mm/dd/yyyy)(optional)',
    'zipcode(optional)',
    'race/ethnicity(optional)',
    'disability(optional)',
    'veteran(optional)',
    'notes',
    'clientid(optional)',
    'program(s)'
];

export const CSV_SNAP_HEADERS = [
    'snap_eligible',
    'snap_enrolled',
    'snap_interest'
];

export const CSV_CAREGIVER_HEADERS = [
    'caregivername(optional)',
    'caregiveremail(optional)',
    'caregiverphonenumber(optional)'
];

interface WelcomeMessageNotesData { isLink: boolean; data: string; link?: string; };

export const WELCOME_MESSAGES: {
    PARA_1: string;
    PARA_2: string;
    PARA_3: string;
    PARA_4: string;
    PARA_5: string;
    PARA_6: string;
    PARA_6_1: string;
    PARA_6_2: string;
    PARA_6_3: string;
    SMS: WelcomeMessageNotesData[];
    Voice: WelcomeMessageNotesData[];
    Email: WelcomeMessageNotesData[];
    FILTER_NOTE: string;
    FILTER_WARN: string;
    ENGLISH_NOTE: string;
} = {
    PARA_1: 'Officially <span class="highlighted-text">Activate your New Users:</span> Send a Welcome Message with correct Opt-out details. Use our AI Function to generate a sample welcome message and review instructions below to be sure you follow regulatory privacy requirements:',
    PARA_2: 'A Welcome Message sets the tone for your new service, uses friendly language and provides regulated opt-out instructions. Send your Welcome Message to each channel [SMS, Voice Call, and Email] - <span class="highlighted-text">one at a time</span>.',
    PARA_3: '<span class="highlighted-text">EXAMPLE:</span> Text “STOP” could not be an option for a user receiving notifications as a Voice call.',
    PARA_4: '<span class="highlighted-text">TIP!</span> Previous Welcome Messages can be found and copied in “Broadcast History” on the Features list.',
    PARA_5: '<span class="highlighted-text">NOTE:</span> Welcome messages will only be sent to those who have not received one. No person(s) will receive more than one - per our system.',
    PARA_6: '<span class="highlighted-text">Recommended language:</span>',
    PARA_6_1: 'Refer to the service as a free/complimentary notification system.',
    PARA_6_2: 'Use your CBO name as an introduction. “Hi, this is [CBO NAME]”',
    PARA_6_3: 'Attaching your call-back number is a dash format to offer support. “For any questions, contact your CBO front desk at 212-xxx-xxxx” Dashes are necessary for correct read back.',
    SMS: [
        {
            isLink: false,
            data: "To view more information about this service, text 'HELP'. If you wish to opt out of this free service, text 'STOP'. See "
        },
        {
            isLink: false,
            data: "Terms: "
        },
        {
            isLink: true,
            data: "gobh.us/terms",
            link: "https://gobh.us/terms"
        },
        {
            isLink: false,
            data: " & "
        },
        {
            isLink: false,
            data: "Privacy Policy: "
        },
        {
            isLink: true,
            data: "gobh.us/privacy",
            link: "https://gobh.us/privacy"
        },
        {
            isLink: false,
            data: ". Msg&Data rates may apply."
        }
    ],
    Voice: [
        {
            isLink: false,
            data: 'You can view our Terms of Use and Privacy Policy at go. blooming. health. dot. com. If you would like to hear additional service options, or unsubscribe from this free service, please call back this number <span class="highlighted-text">[INSERT Blooming Health Number]</span> and select the appropriate menu options. Thanks and have a great day!'
        }
    ],
    Email: [
        {
            isLink: false,
            data: "You can view our "
        },
        {
            isLink: false,
            data: "Terms of Use here: "
        },
        {
            isLink: true,
            data: "gobh.us/terms",
            link: "https://gobh.us/terms"
        },
        {
            isLink: false,
            data: ", and "
        },
        {
            isLink: false,
            data: "Privacy Policy here: "
        },
        {
            isLink: true,
            data: "gobh.us/privacy",
            link: "https://gobh.us/privacy"
        },
        {
            isLink: false,
            data: ". If you wish to be removed from this free service, you can click on the unsubscribe link below."
        }
    ],
    FILTER_NOTE: 'Here you can select multiple program(s) to send welcome messages to members. Please double check to make sure the selections are as intended.',
    FILTER_WARN: 'Since your organization doesn\'t appear to have a program, the welcome message cannot be submitted.',
    ENGLISH_NOTE: 'Enter an English welcome message, and all others will be translated automatically.',

};

export const SNAP_MESSAGES = {
    SNAP_BENEFITS:
        'Here you can select a combination of filters for your broadcast audience. Please double check to make sure the selections are as intended.',
    FILTER_WARN: 'Since your organization doesn\'t appear to have a program, the broadcast message cannot be submitted.'
};

export const MOMENT_EVENT_DATE_FORMATS = {
    parse: {
        dateInput: 'dddd LL'
    },
    display: {
        dateInput: 'dddd LL',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'dddd LL',
        monthYearA11yLabel: 'MMMM YYYY'
    }
};
export const MOMENT_DATE_FORMAT = {
    parse: {
        dateInput: 'LL'
    },
    display: {
        dateInput: 'MM/DD/YYYY',
        monthYearLabel: 'YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'YYYY'
    }
};

export enum appValues {
    authDataKey = 'authData',
    form = 'form',
    alertList = 'alert_list',
    surveyForm = 'survey_form',
    surveyQuestion = 'survey_question',
    defaultSurveyLanguage = 'English',
    welcomeHistory = 'welcome',
    broadcastHistory = 'broadcast',
    orgReportName = 'Organisation Report',
    welcomeMessageParams = 'welcomeMessage',
    broadcastParams = 'sendBroadcast',
    surveyParams = 'sendSurvey',
    individualBroadcastParams = 'Individual Outreach',
    massBroadcastParams = 'Mass Outreach',
    tagColumnName = 'tags',
    referralConByStaff = 'Staff',
    externalPortalUser = 'externalPortalUser',
    orgAdmin = 'orgAdmin',
    bhAdmin = 'bhAdmin',
    superOrgAdmin = 'superOrgAdmin',
    portalUser = "portalUser",
    superAdmin = 'superAdmin',
    activated = 'Activated',
    notActivated = 'Not Activated',
    deactivated = "Deactivated",
    lockedout = 'Locked out',
    lockedoutLabel = 'Locked',
    primaryColor = 'primary',
    accentColor = 'accent',
    noColor = '',
    STATUS_ACTIVE = 'active',
    STATUS_INACTIVE = 'inactive',
}

export enum PortalUserEditFormStatus {
    activate = 'Activate',
    deactivate = 'Deactivate',
}

export enum AppFeatureNames {
    broadcast = 'broadcast',
    survey = 'survey',
    eventReminders = 'reminders'
};

export enum PageUrls {
    portalUsers = '/home/portal-users'
}

export const APP_PLAN_TIERS_NAME = {
    FREE: 'freemium'
};
export const FREEMIUM_TIER_DISABLED_URLS = [
    'program-reminder'
];

export enum SurveyViews {
    preview = 'preview',
    confirm = 'confirm',
    historyPreview = 'historyPreview',
    rerunPreview = 'rerunPreview',
    rerunConfirm = 'rerunConfirm'
}

export const DefaultOrgLanguages = ['English', 'Spanish', 'Chinese', 'Russian'];
export const DefaultOrgLogo = 'assets/images/BH_logo_1.png';

export const BroadcastFilterMethod: { [key: string]: { method: 'mass' | 'individual', label: string } } = {
    massBroadcast: {
        method: 'mass',
        label: 'Mass Outreach'
    },
    individualBroadcast: {
        method: 'individual',
        label: 'Individual Outreach'
    }
};
export const BroadcastPreviewLabels = {
    attachments: 'Attachments',
    audience: "Audience",
    orgLabels: "Organization Label(s)",
    languages: "Languages",
    channels: "Channels",
    interests: "Interests",
    snap_summary: "SNAP Benefits",
    caregiver_summary: "Care Givers",
    schedule_time: "Schedule Date & Time",
    eligiblecount: "Eligible Count",
    tags_summary: "Tags",
};
export const SurveyPreviewLabels = {
    centers: "Audience",
    member_ref: "Audience",
    languages: "Languages",
    channels: "Channels",
    interest_summary: "Interests",
    snap_summary: "SNAP Benefits",
    caregiver_summary: "Care Givers",
    schedule_time: "Schedule Date & Time",
    eligiblecount: "Eligible Count",
};

export const ChannelKeyValues = {
    sms: 'SMS',
    voice: 'Voice Call',
    email: 'E-Mail'
}
export const PreferredChannels: { [key: string]: string } = {
    'checkin_email': 'Email',
    'checkin_voice': 'Voice Call',
    'checkin_sms': 'SMS'
}

export const ChannelLabels: { [key: string]: string } = {
    'sms': 'SMS',
    'voice': 'Voice Call',
    'email': 'Email'
}

export const BroadcastStatus = {
    inProgress: {
        class: 'bg-accent text-accent',
        value: 'In-Progress'
    },
    completed: {
        class: 'bg-grey',
        value: 'Completed'
    },
    error: {
        class: 'bg-warn text-warn',
        value: 'Error'
    },
    queued: {
        class: 'bg-accent text-accent',
        value: 'Queued',
        label: 'Scheduled'
    },
    failed: {
        class: 'bg-warn text-warn',
        value: 'Failed',
    },
    cancelled: {
      class: 'bg-warn text-warn',
      value: 'Cancelled',
    },
    draft: {
        class: 'bg-grey',
        value: 'Draft'
    }
};

export enum referralPageView {
    register = 'register',
    create = 'create',
    prseSubmission = 'preSubmission',
    detail = 'detail'
}
export enum referralStatuses {
    draft = 'DRAFT',
    submitted = 'SUBMITTED',
    resolved = 'RESOLVED',
    unresolved = 'UNRESOLVED',
    unknown = 'UNKNOWN',
    cancelled = 'CANCELLED',
    //Auto statuses
    provider_selection_completed = 'PROVIDER_SELECTION_COMPLETED',
    awaiting_consent = 'AWAITING_CONSENT',
    consent_declined = 'CONSENT_DECLINED'
};

export const SelfCaregiverOptions: { label: string; value: SelfCareGiverFormData }[] = [
    {
        label: 'Yes',
        value: 'Yes'
    },
    {
        label: 'No',
        value: 'No'
    },
    {
        label: 'Unsure',
        value: 'Unsure'
    },
    {
        label: 'Not Indicated',
        value: 0
    }
];

//Search Bar values
export const SEARCH_BAR_CONFIG: { [key: string]: CustomSearchBarConfig } = {
    'USERS': {
        input: {
            value: '',
            placeholder: 'Search'
        },
        searchBtn: { icon: 'search', iconClass: 'material-icons-outlined' },
        searchByOptions: [
            { value: 'name', name: 'Name' },
            { value: 'member_ref', name: 'Member ID' },
            { value: 'phone_no', name: 'Phone Number' },
            { value: 'org_client_id', name: 'Org. Client ID' }
        ],
        selectedSearchByOptions: { value: 'name', name: 'Name' }
    },
    'SURVEY': {
        input: {
            value: '',
            placeholder: 'Search by Name'
        },
        searchBtn: { icon: 'search', iconClass: 'material-icons-outlined' },
        searchByOptions: [
            { value: 'name', name: 'Name' },
        ],
        selectedSearchByOptions: { value: 'name', name: 'Name' }
    },
    'WORKFLOW': {
        input: {
            value: '',
            placeholder: "Search by Name"
        },
        searchBtn: { icon: 'search', iconClass: 'material-icons-outlined' },
        searchByOptions: [
            { value: 'name', name: 'Name' },
        ],
        selectedSearchByOptions: { value: 'name', name: 'Name' }
    },
    'REFERRALS': {
        input: {
            value: '',
            placeholder: 'Search'
        },
        searchBtn: { icon: 'search', iconClass: 'material-icons-outlined' },
        searchByOptions: [
            { value: 'name', name: 'Name' },
            { value: 'member_ref', name: 'Member ID' }
        ],
        selectedSearchByOptions: { value: 'name', name: 'Name' }
    },
    'PORTAL_USERS': {
        input: {
            value: '',
            placeholder: 'Search'
        },
        searchBtn: { icon: 'search', iconClass: 'material-icons-outlined' },
        searchByOptions: [
            { value: 'name', name: 'Name' },
            { value: 'email', name: 'Email' }
        ],
        selectedSearchByOptions: { value: 'name', name: 'Name' }
    },
    'BROADCAST': {
        input: {
            value: '',
            placeholder: 'Search by Message'
        },
        searchBtn: { icon: 'search', iconClass: 'material-icons-outlined' },
        searchByOptions: [{ value: 'message', name: 'Message' }],
        selectedSearchByOptions: { value: 'message', name: 'Message' }
    },
    'BROADCAST_HISTORY_USERS': {
        input: {
            value: '',
            placeholder: 'Search'
        },
        searchBtn: { icon: 'search', iconClass: 'material-icons-outlined' },
        searchByOptions: [
            { value: 'member_ref', name: 'Member ID' },
            { value: 'fname', name: 'First Name' },
            { value: 'lname', name: 'Last Name' },
        ],
        selectedSearchByOptions: { value: 'member_ref', name: 'Member ID' }
    },
    'PROGRAM': {
        input: {
            value: '',
            placeholder: 'Search'
        },
        searchBtn: { icon: 'search', iconClass: 'material-icons-outlined' },
        searchByOptions: [
            { value: 'name', name: 'Event Name' },
            { value: 'program_ref', name: 'Event ID' },
        ],
        selectedSearchByOptions: { value: 'name', name: 'Event Name' }
    },
    'REMINDER': {
        input: {
            value: '',
            placeholder: 'Search by reminder date (eg: 01/01/2020)'
        },
        searchBtn: { icon: 'search', iconClass: 'material-icons-outlined' },
        searchByOptions: [
            { value: 'reminder_date', name: 'Reminder date' },
        ],
        selectedSearchByOptions: { value: 'reminder_date', name: 'Reminder date' }
    }
};

export const DEFAULT_TIMEZONE = {
    timezone_abbr: "EST",
    timezone_label: "Eastern Time",
    hour_offset: "-5",
    timezone_identifier: "America/New_York"
};

export const DATE_TIME_FORMATS = {
    mediumDateTime: 'MM/DD/YY hh:mm A',
    mediumDateTimeWithDay: 'dddd MM/DD/YY hh:mm A',
    mediumDateTimeWithMinDay: 'ddd MM/DD/YY hh:mm A',
    fullDateInNum: 'MM/DD/YYYY',
    fullDateInNumWithHyphen: 'MM-DD-YYYY',
    fullTime: 'hh:mm A',
    fullDateWithMinDay: 'ddd MMMM DD, YYYY',
    fullDateTimeWithDay: 'dddd, MMMM DD, YYYY hh:mm A',
    fullMonthShortDateFullYear: 'MMM D, YYYY', // Jan 12, 2025
    fullDateInNumFormat: 'YYYY-MM-DD'
}

/**TABLE FILTERS DATA */
export const TABLE_FILTER_DATA: { [key: string]: TableFilterData[] } = {
    "USERS_LIST": [
        {
            label: "Unsubscribed",
            value: "unsubscribed",
            multiSelect: false,
            showLabelWithSelected: true,
            options: [
                {
                    label: 'Yes',
                    value: 'true',
                    checked: false
                },
                {
                    label: 'No',
                    value: 'false',
                    checked: false
                }
            ]
        },
        {
            label: "Status",
            value: "active",
            multiSelect: false,
            options: [
                {
                    label: 'Active',
                    value: 'true',
                    checked: false
                },
                {
                    label: 'Inactive',
                    value: 'false',
                    checked: false
                }
            ]
        },
        {
            label: "Gender",
            value: "gender",
            multiSelect: false,
            options: [
                {
                    label: 'Male',
                    value: 'male',
                    checked: false
                },
                {
                    label: 'Female',
                    value: 'female',
                    checked: false
                },
                {
                    label: 'Non-binary',
                    value: 'nonbinary',
                    checked: false
                },
                {
                    label: 'Unspecified',
                    value: 'unspecified',
                    checked: false
                }
            ]
        },
        {
            label: "Role",
            value: "household_role",
            multiSelect: false,
            options: [
                {
                    label: 'Household Member',
                    value: 'household_member',
                    checked: false
                },
                {
                    label: 'Primary User',
                    value: 'primary_user',
                    checked: false
                }
            ]
        },
    ],
    "REFERRAL_LIST": [
        {
            label: "Needs",
            value: "need",
            multiSelect: false,
            options: []
        },
        {
            label: "Referred to",
            value: "referred_to",
            multiSelect: false,
            options: []
        },
        {
            label: "Status",
            value: "status",
            multiSelect: false,
            options: []
        },
    ],
    "INCOMMING_REFERRAL_LIST": [
        {
            label: "Needs",
            value: "need",
            multiSelect: false,
            options: []
        },
        {
            label: "Status",
            value: "status",
            multiSelect: false,
            options: []
        },
    ],
    "BROADCAST_LIST": [
        {
            label: "Language",
            value: "language",
            multiSelect: false,
            options: []
        }
    ],
    "BROADCAST_HISTORY_LIST": [
        {
            label: "Type",
            value: "type",
            multiSelect: false,
            options: [
                {
                    label: 'Welcome',
                    value: 'welcome',
                    checked: false
                },
                {
                    label: 'Broadcast',
                    value: 'broadcast',
                    checked: false
                }
            ]
        },
        {
            label: "Language",
            value: "language",
            multiSelect: false,
            options: []
        }
    ],
    "BROADCAST_HISTORY_USERS_LIST": [
        {
            label: "Language",
            value: "language",
            multiSelect: false,
            options: []
        },
        {
            label: "Channels",
            value: "channel",
            multiSelect: false,
            options: [
                {
                    checked: false,
                    label: 'SMS',
                    value: 'sms'
                },
                {
                    checked: false,
                    label: 'Email',
                    value: 'email'
                },
                {
                    checked: false,
                    label: 'Voice Call',
                    value: 'voice'
                }
            ]
        },
    ],
    "SURVEY_LIST": [
        {
            label: "Status",
            value: "active",
            multiSelect: false,
            options: [
                {
                    label: 'Active',
                    value: 'true',
                    checked: false
                },
                {
                    label: 'Inactive',
                    value: 'false',
                    checked: false
                }
            ]
        }
    ],
    "SURVEY_HISTORY_USERS_LIST": [
        {
            label: "Language",
            value: "language",
            multiSelect: false,
            options: []
        },
        {
            label: "Channels",
            value: "channel",
            multiSelect: false,
            options: [
                {
                    checked: false,
                    label: 'SMS',
                    value: 'sms'
                },
                {
                    checked: false,
                    label: 'Voice Call',
                    value: 'voice'
                }
            ]
        },
    ],
    "PROGRAM_LIST": [
        {
            label: "Status",
            value: "active",
            multiSelect: false,
            options: [
                {
                    label: 'Active',
                    value: 'true',
                    checked: false
                },
                {
                    label: 'Inactive',
                    value: 'false',
                    checked: false
                }
            ]
        }
    ],
    "PORTAL_USER": [
        {
            label: "Status",
            value: "status",
            multiSelect: false,
            options: [
                {
                    label: "Not Activated",
                    value: "not_activated",
                    checked: false
                },
                {
                    label: "Activated",
                    value: "activated",
                    checked: false
                },
                {
                    label: "Locked Out",
                    value: "locked",
                    checked: false
                },
                {
                    label: "Deactivated",
                    value: "deactivated",
                    checked: false
                }
            ]
        }
    ],
    "WORKFLOW": [
        {
            label: "Status",
            value: "status",
            multiSelect: false,
            options: [
                {
                    label: "Active",
                    value: "ACTIVE",
                    checked: false,
                },
                {
                    label: "Paused",
                    value: "PAUSED",
                    checked: false,
                },
                {
                    label: "Draft",
                    value: "DRAFT",
                    checked: false,
                }
            ]
        }
    ]
};

export const SideNavBarDisabledLinksData: string[] = [
    '/not-found',
    '/home/referrals'
];
export const SIDENAV_DISABLED_ROUTES = {
    NOT_FOUND: '/not-found',
    REFERRALS: '/home/referrals',
    WORKFLOW: '/home/workflows',
}

export const SideNavBarLinksData: NavLinksGroup = {
    navs: [
        {
            route: ['/home/dashboard'],
            label: 'Dashboard',
            icon: 'dashboard',
            featureName: 'DASHBOARD',
            exactActiveCheck: true
        },
        {
            route: ['/home/broadcast'],
            label: 'Broadcast',
            icon: 'podcasts',
            featureName: 'SEND_BROADCAST',
            subRoutes: [
                {
                    route: ['/home/broadcast/list'],
                    label: 'Scheduled Broadcasts',
                    featureName: 'SEND_BROADCAST',
                },
                {
                    route: ['/home/broadcast/sendBroadcast'],
                    label: 'Send Broadcast',
                    featureName: 'SEND_BROADCAST',
                },
                // {
                //     route: ['/home/broadcast/welcome-list'],
                //     label: 'Scheduled Welcome Messages'
                // },
                //   {
                //     route: ['/home/broadcast/welcomeMessage'],
                //     label: 'Send Welcome',
                //     featureName: 'SEND_WELCOME',
                //   },
                {
                    route: ['/home/broadcast/history'],
                    label: 'History',
                    featureName: 'BROADCAST_HISTORY',
                }
            ]
        },
        {
            route: ['/home/program-reminder'],
            label: 'Event Reminders',
            featureName: 'EVENT_REMINDERS',
            icon: 'notifications',
            subRoutes: [
                {
                    route: ['/home/program-reminder/history'],
                    label: 'History',
                    featureName: 'EVENT_REMINDERS_HISTORY',
                    linkActiveCheck: '/home/program-reminder/history'
                },
            ]
        },
        {
            route: ['/home/survey'],
            label: 'Survey',
            featureName: 'SURVEY',
            icon: 'apps',
            subRoutes: [
                {
                    route: ['/home/survey'],
                    label: 'Create/Edit Survey',
                    featureName: 'SURVEY',
                    linkActiveCheck: '/home/survey'
                },
                {
                    route: ['/home/survey', 'execution'],
                    featureName: 'SEND_SURVEY',
                    label: 'Send Survey'
                },
                {
                    route: ['/home/survey', 'scheduled'],
                    featureName: 'SURVEY',
                    label: 'Scheduled Surveys'
                },
                {
                    route: ['/home/survey/history'],
                    label: 'History & Rerun',
                    featureName: 'SURVEY_HISTORY',
                }
            ]
        },
        {
            route: ['/home/workflows'],
            label: 'Workflows',
            featureName: 'WORKFLOWS',
            icon: 'automation',
            iconSymbol: true,
            subRoutes: [
                {
                    route: ['/home/workflows'],
                    label: 'Create/Edit Workflows',
                    featureName: 'WORKFLOWS',
                    linkActiveCheck: '/home/workflows'
                },
                {
                    route: ['/home/workflows', 'archived'],
                    label: 'Archived Workflows',
                    featureName: 'WORKFLOWS'
                }
            ]
        },
    ],
    admin: [
        {
            route: ['/home/referrals'],
            label: 'Referrals',
            featureName: 'EXTERNAL_REFERRAL_MANAGEMENT',
            icon: 'format_list_bulleted',
            subRoutes: [
                {
                    route: ['/home/referrals/outgoing-referrals'],
                    featureName: 'REFERRAL_MANAGEMENT',
                    label: 'Outgoing Referrals'
                },
                {
                    route: ['/home/referrals/incoming-referrals'],
                    featureName: 'EXTERNAL_REFERRAL_MANAGEMENT',
                    label: 'Incoming Referrals'
                }
            ]
        },
        {
            route: ['/home/users'],
            label: 'Users',
            featureName: 'CLIENT_USERS',
            icon: 'people'
        },
        {
            route: ['/home/portal-users'],
            label: 'Portal Users',
            featureName: 'PORTAL_USERS',
            icon: 'manage_accounts',
        }
    ],
    system: [
        {
            route: ['/home/setting'],
            label: 'Settings',
            featureName: 'SETTINGS',
            icon: 'admin_panel_settings',
        }
    ]
};

export const RefUserNavBarLinksData: CustomNavLink = {
    route: ['/referrals/incoming-referrals'],
    label: 'Referrals',
    icon: 'format_list_bulleted',
    featureName: 'REFERRAL_MANAGEMENT',
};

export const COMM_TYPES = {
    broadcast: "broadcast",
    broadcastSch: "broadcast scheduled",
    surveyFirstRunSch: "survey first run scheduled",
    surveyCompleted: "survey completed",
    surveyRerunSch: "survey rerun scheduled"
}

export const CommHistoryUserColumnData: CustomTableColumns[] = [
    {
        name: 'Member ID',
        value: 'member_ref',
        headerClasses: 'short',
        descriptionClasses: 'short',
        hasSort: true
    },
    {
        name: 'First Name',
        value: 'fname',
        headerClasses: 'medium',
        descriptionClasses: 'medium',
        hasSort: true
    },
    {
        name: 'Last Name',
        value: 'lname',
        headerClasses: 'medium',
        descriptionClasses: 'medium',
        hasSort: true
    },
    {
        name: 'Language',
        value: 'lang',
        headerClasses: 'medium',
        descriptionClasses: 'medium',
        hasSort: true
    },
    {
        name: 'Channel',
        value: 'channel',
        headerClasses: 'large',
        descriptionClasses: 'large',
        hasSort: true
    },
    {
        name: 'Date Time',
        value: 'timestamp',
        headerClasses: 'large',
        descriptionClasses: 'large',
        hasSort: true
    }
];

export const REFERRAL_AUTH_VIEWS: { [k: string]: ReferralViewType } = {
    WELCOME: 'WELCOME',
    NEEDS_CONFIRM: 'NEEDS_CONFIRM',
    FEEDBACK: 'FEEDBACK',
    PERSON_CONFIRM: 'PERSON_CONFIRM',
    VERIFY_PERSON: 'VERIFY_PERSON',
    END: 'END',
}

export const NoProgramFilterValue = { id: 0, name: 'No Program' };
export const NoAffiliationsFilterValue = { id: 0, name: 'No affiliations' };

export const ComponentColorCodes = {
    chartPrimary: '#4AE1CA',
    chartSecondary: '#d7d7d7',
};
export const SURVEY_DASHBOARD_CHARTS_DATA = {
    responsesChart: {
        elementID: 'response-chart',
        columns: [['string', 'Responses'], ['number', 'count']],
        rows: ['Responded', 'Not Responded'],
        options: {
            pieHole: 0.8,
            colors: [ComponentColorCodes.chartPrimary, ComponentColorCodes.chartSecondary],
            legend: 'none',
            chartArea: {
                left: 8,
                top: 8,
                right: 16,
                bottom: 8,
            },
            pieSliceText: 'none',
        }
    },
    responseDatesChart: (maxTicks: number, xLength: number, annotationsEnabled: boolean = false, ticks: number[] = []) => ({
        elementID: 'response-date-chart',
        columns: [['date', 'Date of survey response'], ['number', 'No. of Users']],
        options: {
            width: (xLength > 5) ? 600 : undefined,
            bar: (xLength <= 5) ? { width: 20 } : undefined,
            axisTitlesPosition: 'none',
            hAxis: {
                format: 'MM/dd',
            },
            vAxis: (maxTicks <= 5) ? {
                ticks: [0, 1, 2, 3, 4, 5],
            } : {
                ticks: ticks,
            },
            chartArea: {
                left: 40,
                top: 8,
                right: 30,
                bottom: 26,
            },
            colors: [ComponentColorCodes.chartPrimary],
            legend: 'none',
            series: {
                0: {
                    type: 'bars'
                },
            },
            vAxes: {
                0: {}, // Primary y-axis for bars
                1: {} // Secondary y-axis for line series
            },
            annotations: !annotationsEnabled
                ? {}
                : {
                    textStyle: {
                        fontSize: 10,
                        bold: true,
                        italic: false,
                        color: 'black',

                    },
                    style: 'line',
                    stem: { length: 0 },
                    alwaysOutside: true, // Annotations will be placed directly above the line
                }
        }
    })
}

export const MANAGE_INTERESTS_DATA = {
    programs: {
        title: 'Users',
        subtitle: 'Number of users associated with interest categories.',
        tableColumns: [
            { name: 'Program', value: 'name', class: 'xlarge' },
            { name: 'Active Users', value: 'activeCount', class: 'medium' },
            { name: 'Inactive Users', value: 'inactiveCount', class: 'medium' },
            { name: 'Total Users', value: 'totalCount', class: 'medium' },
        ],
        tableCols: ['name', 'activeCount', 'inactiveCount', 'totalCount'],
    },
    surveys: {
        title: 'Surveys',
        subtitle: 'Surveys associated with interest categories.',
        tableColumns: [
            { name: 'Instance', value: 'instance_id', class: 'medium' },
            { name: 'Name', value: 'name', class: 'medium' },
            { name: 'Audience', value: 'audience', class: 'xlarge' },
            { name: 'Last Run Date', value: 'last_run_date', class: 'medium' }
        ],
        tableCols: ['instance_id', 'name', 'audience', 'last_run_date'],
    },
    broadcasts: {
        title: 'Broadcasts',
        subtitle: 'Broadcasts associated with interest categories.',
        tableColumns: [
            { name: 'ID', value: 'id', class: 'short' },
            { name: 'Message', value: 'message', class: 'x2large' },
            { name: 'Audience', value: 'audience', class: 'medium' },
            { name: 'Sent Date', value: 'sent_date', class: 'short' },
        ],
        tableCols: ['id', 'message', 'audience', 'sent_date'],
    },
    reminders: {
        title: 'Event Reminders',
        subtitle: 'Program events associated with interest categories.',
        tableColumns: [
            { name: 'ID', value: 'program_ref', class: 'short' },
            { name: 'Name', value: 'name', class: 'medium' },
            { name: 'Audience', value: 'program', class: 'large' },
            { name: 'Active', value: 'active', class: 'short' },
            { name: 'Sent Date', value: 'sent_date', class: 'short' },
        ],
        tableCols: ['program_ref', 'name', 'program', 'active', 'sent_date'],
    }
}

export const SURVEY_HISTORY_STATUS = {
    noUsers: 'No Users',
    inProgress: 'In-Progress',
    completed: 'Completed',
    error: 'Error',
    failed: 'Failed'
}

export enum SurveyConditionalValues {
    next = 'next',
    end = 'end',
    option = 'Option',
    reset = 'reset',
    undo = 'undo',
    delete = 'delete',
    discard = 'discard'
}

export enum UserStatus {
    yes = 'Yes',
    no = 'No'
}

// Tableau server URLs
export const TABLEAU_URLS = {
    UK: 'https://prod-uk-a.online.tableau.com',
    US: 'https://us-east-1.online.tableau.com',
    SCRIPT_UK: 'https://prod-uk-a.online.tableau.com/javascripts/api/tableau.embedding.3.latest.min.js',
    SCRIPT_US: 'https://us-east-1.online.tableau.com/javascripts/api/tableau.embedding.3.latest.min.js',
    HOST_UK: 'prod-uk-a.online.tableau.com',
    HOST_US: 'us-east-1.online.tableau.com'
};

export const APP_CSP = {
    BASE: `
      default-src 'self' https://fonts.googleapis.com https://www.google.co.in ${TABLEAU_URLS.UK} ${TABLEAU_URLS.US} https://bloominghealthbucket.s3.us-east-2.amazonaws.com;
      font-src 'self' https://fonts.intercomcdn.com https://fonts.googleapis.com https://fonts.gstatic.com ${TABLEAU_URLS.UK} ${TABLEAU_URLS.US};
      style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://www.gstatic.com ${TABLEAU_URLS.UK} ${TABLEAU_URLS.US}; 
      script-src 'self' https://www.gstatic.com https://www.googletagmanager.com https://analytics.google.com https://stats.g.doubleclick.net https://www.google.co.in https://analytics.google.com ${TABLEAU_URLS.UK} ${TABLEAU_URLS.US} ${TABLEAU_URLS.UK}/javascripts/api/tableau.embedding.3.latest.min.js ${TABLEAU_URLS.US}/javascripts/api/tableau.embedding.3.latest.min.js https://js-agent.newrelic.com;
      object-src 'none';
      frame-src 'self' https://intercom-sheets.com https://fonts.googleapis.com https://td.doubleclick.net https://datastudio.google.com https://lookerstudio.google.com ${TABLEAU_URLS.UK} ${TABLEAU_URLS.US};
      base-uri 'self';
      img-src * 'self' data:;
      script-src-elem 'self' ${TABLEAU_URLS.UK}/javascripts/api/tableau.embedding.3.latest.min.js ${TABLEAU_URLS.US}/javascripts/api/tableau.embedding.3.latest.min.js https://js.intercomcdn.com https://widget.intercom.io https://www.googletagmanager.com https://www.gstatic.com https://js-agent.newrelic.com;
      worker-src 'self' blob:;
      media-src 'self' https://us-east1-jenny1-1-aglley.cloudfunctions.net https://bloominghealthbucket.s3.us-east-2.amazonaws.com https://p-twilio-event-stream-service-dqijxo462q-ue.a.run.app data: blob:;
    `,
    CONNECT: `connect-src 'self' https://storage.cloud.google.com https://api-iam.intercom.io/messenger/web/ping https://api-iam.intercom.io wss://primary-realtime.intercom-messenger.com wss://nexus-websocket-b.intercom.io wss://nexus-websocket-a.intercom.io data: https://fonts.gstatic.com https://stats.g.doubleclick.net https://www.gstatic.com https://fonts.googleapis.com https://firebase.googleapis.com https://firebaseinstallations.googleapis.com https://firebaseremoteconfig.googleapis.com https://analytics.google.com https://firebaselogging-pa.googleapis.com ${TABLEAU_URLS.UK} ${TABLEAU_URLS.US} https://bam.nr-data.net`
};

export const ADMIN_ROLE_LABELS = {
    'externalPortalUser': 'External Portal User',
    'orgAdmin': 'Org Admin',
    'bhAdmin': 'BH Admin',
    'superOrgAdmin': 'Super Org Admin',
    'portalUser': 'Portal User'
}

export const FEATURES = {
    DASHBOARD: "DASHBOARD",
    SEND_BROADCAST: "SEND_BROADCAST",
    SEND_WELCOME: "SEND_WELCOME",
    BROADCAST_HISTORY: "BROADCAST_HISTORY",
    EVENT_REMINDERS: "EVENT_REMINDERS",
    EVENT_REMINDERS_HISTORY: "EVENT_REMINDERS_HISTORY",
    SURVEY: "SURVEY",
    WORKFLOW: "WORKFLOWS",
    SEND_SURVEY: "SEND_SURVEY",
    SURVEY_HISTORY: "SURVEY_HISTORY",
    REFERRAL_MANAGEMENT: "REFERRAL_MANAGEMENT",
    EXTERNAL_REFERRAL_MANAGEMENT: "EXTERNAL_REFERRAL_MANAGEMENT",
    CLIENT_USERS: "CLIENT_USERS",
    PORTAL_USERS: "PORTAL_USERS",
    SETTINGS: "SETTINGS",
    MANAGE_INTERESTS: "MANAGE_INTERESTS",
    ENTERPRISE_PORTAL: "ENTERPRISE_PORTAL",

    PORTAL_USERS_CREATE: "PORTAL_USERS_CREATE",
    PORTAL_USERS_RESET_PASSSORD: "PORTAL_USERS_RESET_PASSSORD",
    PORTAL_USERS_RESEND_ACTIVATION: "PORTAL_USERS_RESEND_ACTIVATION",
    PORTAL_USERS_STATUS_UPDATE: "PORTAL_USERS_STATUS_UPDATE",
    PORTAL_USERS_ROLE_UPDATE: 'PORTAL_USERS_ROLE_UPDATE',
    PORTAL_USERS_RE_ACTIVATE: "PORTAL_USERS_RE_ACTIVATE",
    PORTAL_USERS_DEACTIVATE: "PORTAL_USERS_DEACTIVATE",
}

export const ACCESS_LEVELS = {
    FULL: "full",
    NO_ACCESS: "no_access",
    CREATE_EDIT: "create/edit",
    VIEW: "view"
}

export const DEFAULT_WORKFLOW_TITLE = "Untitled Workflow 1";

export const WORKFLOW_GUIDED_TOURS = {
    TRIGGER_BLOCK_ANIMATION: 'Trigger Block Animation',
    LINE_CONNECT_ANIMATION: 'Line Connect Animation',
    CONFIG_PANEL_ANIMATION: 'Config Panel Animation',
    ACTION_BLOCK_ANIMATION: 'Action Block',
    CHANNEL_PREFERENCE_ANIMATION: 'Channel Preference',
}
export const USER_TRIGGER_FORM_CONSTANTS = {
    CHOOSE_ALLOWED_CHANGE_SOURCES: 'Choose all the allowed change sources'
};

export const VALIDATION_LIMIT = {
    FIFTY: 50
}

export const CLIENT_PROFILE_FORM_CHAR_LIMITS: { [field: string]: number } = {
    F_NAME_MAX: VALIDATION_LIMIT.FIFTY,
    L_NAME_MAX: VALIDATION_LIMIT.FIFTY,
    M_NAME_MAX: VALIDATION_LIMIT.FIFTY,
}

export const CUSTOM_FIELD_TYPES_LABELS = {
    single: 'Single-Select',
    multi: 'Multi-Select',
    date: 'Date',
};
export const CUSTOM_FIELD_TYPES_VALUES = {
    single: 'single',
    multi: 'multi',
    date: 'date',
};
export const CUSTOM_FIELD_STATUS_VALUES: { [status: string]: CustomFieldStatus } = {
    active: 'active',
    archived: 'archived',
};
export const CUSTOM_FIELD_TYPES: CustomFieldTypeOption[] = [
    {
        label: CUSTOM_FIELD_TYPES_LABELS.single,
        value: CUSTOM_FIELD_TYPES_VALUES.single
    },
    {
        label: CUSTOM_FIELD_TYPES_LABELS.multi,
        value: CUSTOM_FIELD_TYPES_VALUES.multi
    },
    {
        label: CUSTOM_FIELD_TYPES_LABELS.date,
        value: CUSTOM_FIELD_TYPES_VALUES.date
    },
];

export const BH_DATE = {
    FORMAT: 'YYYYMMDD_HHmmss',
}

export const BH_Y_N_NOT_INDICATED_OPTIONS = ['Yes', 'No', 'Not Indicated'];
export const BH_OPTIONS = {
    GENDER: ['Male', 'Female', 'Non-binary', 'Unspecified'],
    DISABILITY: BH_Y_N_NOT_INDICATED_OPTIONS,
    VETERAN: BH_Y_N_NOT_INDICATED_OPTIONS,
    SNAP_ELIGIBLE: BH_Y_N_NOT_INDICATED_OPTIONS,
    SNAP_ENROLLED: BH_Y_N_NOT_INDICATED_OPTIONS,
    SNAP_INTEREST: BH_Y_N_NOT_INDICATED_OPTIONS,
    IC_CHANNELS: ['SMS', 'Voice', 'Email']
}
